import { isConfigFlagEnabled } from '../../config';
import { ALL_BOMB_PROPERTIES } from '../../types/bombTypes';
import { throwBomb } from '../../utils/bombUtils';
import { MINIMUM_PLAYER_ANIMATION_DURATION_MS } from '../../utils/playerAnimationUtils';
import { maybePlayBombSplatSound, maybePlayBombThrowSound } from '../../utils/soundUtils';
import { calcTrajectory, getVelocityComponentsAtTime } from '../../utils/trajectoryUtils';
import { getFromSeatBounds } from '../../utils/trajectoryUtils';
import { delay, isPageVisible } from '../../utils/utils';
import { getFromX } from '../reducers/bombsReducer';
import { addPlayerAnimation, addTableShakeAnimation } from './animationsActions';
export const CLEAR_BOMBS_STATE = 'CLEAR_BOMBS_STATE';
export const ADD_BOMB = 'ADD_BOMB';
export const ADD_BOMB_SPLAT = 'ADD_BOMB_SPLAT';
export const REMOVE_BOMB = 'REMOVE_BOMB';
export const REMOVE_BOMB_SPLAT = 'REMOVE_BOMB_SPLAT';
export const REMOVE_ALL_PLAYER_BOMB_SPLATS = 'REMOVE_ALL_PLAYER_BOMB_SPLATS';
export const REMOVE_ALL_BOMB_SPLATS = 'REMOVE_ALL_BOMB_SPLATS';
export const RECALCULATE_BOMB_TRAJECTORIES = 'RECALCULATE_BOMB_TRAJECTORIES';
export const SET_DRAGGING_BOMB_TYPE = 'SET_DRAGGING_BOMB_TYPE';
export const SET_DRAGGING_BOMB_IS_OVER_TARGET = 'SET_DRAGGING_BOMB_IS_OVER_TARGET';
const MULTI_BOMB_INTERVAL_MS = 250;
export function clearBombsState() {
    return {
        type: CLEAR_BOMBS_STATE
    };
}
function addBomb(bombType, fromSeatIndex, toSeatIndex, toSeatBounds, fromCoords, trajectory, tableAreaDimensions) {
    return {
        type: ADD_BOMB,
        payload: {
            bombType,
            fromSeatIndex,
            toSeatIndex,
            toSeatBounds,
            fromCoords,
            trajectory,
            tableAreaDimensions
        }
    };
}
export const thunkAddBombFromInbound = (inbound) => {
    return async function (dispatch, getState) {
        if (!isPageVisible()) {
            // don't add new bomb animations when page/tab is not visible
            return;
        }
        const delayMs = inbound.delay * MULTI_BOMB_INTERVAL_MS;
        if (delayMs) {
            // this bomb is intended to be delayed (usually because multiple bombs were fired at the same time)
            await delay(delayMs);
        }
        const state = getState();
        if (state.scene.sceneType !== 'poker') {
            // unsupported scene
            console.warn('Cannot bomb outside of the poker scene');
            return;
        }
        const { allSeatBounds, tableAreaDimensions } = state.gameDimensions;
        if (!allSeatBounds.length) {
            // not initialized, can't bomb
            console.warn('Seat bounds not initialized, could not calculate bomb trajectory');
            return;
        }
        if (!tableAreaDimensions) {
            // not initialized, can't bomb
            console.warn('Table area dimensions not initialized, could not calculate bomb trajectory');
            return;
        }
        const { bombType, toSeatIndex, fromSeatIndex } = inbound;
        if (!bombType) {
            console.warn("Bomb type wasn't provided or wasn't defined");
            return;
        }
        if (!state.session.loadedBombs[bombType]) {
            console.warn(`Bomb type ${bombType} hasn't loaded yet`);
            return;
        }
        if (toSeatIndex === undefined) {
            console.warn('Bombs that have an undefined toSeatIndex (bombing the dealer) are not yet supported');
            return;
        }
        const fromSeatBounds = getFromSeatBounds(fromSeatIndex, allSeatBounds, tableAreaDimensions);
        const toSeatBounds = allSeatBounds[toSeatIndex];
        const fromCoords = {
            x: getFromX(fromSeatBounds, toSeatBounds),
            y: fromSeatBounds.y
        };
        const trajectory = calcTrajectory(fromCoords, toSeatBounds);
        if (trajectory) {
            dispatch(addBomb(bombType, fromSeatIndex, toSeatIndex, toSeatBounds, fromCoords, trajectory, tableAreaDimensions));
            if (fromSeatIndex !== undefined) {
                // show "recoil" animation from throwing a bomb
                const { velocityX, velocityY } = getVelocityComponentsAtTime(0, trajectory.velocity, trajectory.radians, toSeatBounds.x > fromCoords.x);
                dispatch(addPlayerAnimation(fromSeatIndex, {
                    type: 'throw-recoil',
                    velocityX: -velocityX,
                    velocityY: -velocityY,
                    strength: 1,
                    durationMs: Math.max(500, MINIMUM_PLAYER_ANIMATION_DURATION_MS)
                }));
            }
        }
        maybePlayBombThrowSound(getState());
    };
};
function addBombSplat(bombSplat) {
    return {
        type: ADD_BOMB_SPLAT,
        payload: bombSplat
    };
}
function maybeAddTableShakeAnimation(dispatch, getState, bombSplat) {
    if (!isConfigFlagEnabled('tableShakeAnimations')) {
        return;
    }
    if (getState().poker.mySeatIndex !== bombSplat.seatIndex) {
        return;
    }
    const { tableShakeProperties } = ALL_BOMB_PROPERTIES[bombSplat.bombType];
    if (!tableShakeProperties) {
        return;
    }
    dispatch(addTableShakeAnimation(tableShakeProperties));
}
export const thunkAddBombSplat = (bombSplat) => {
    return function (dispatch, getState) {
        if (!isPageVisible()) {
            // don't add new bomb animations when page/tab is not visible
            return;
        }
        dispatch(addBombSplat(bombSplat));
        maybeAddTableShakeAnimation(dispatch, getState, bombSplat);
        maybePlayBombSplatSound(getState(), bombSplat.bombType);
    };
};
export function removeBomb(id) {
    return {
        type: REMOVE_BOMB,
        payload: id
    };
}
export function removeBombSplat(seatIndex, id) {
    return {
        type: REMOVE_BOMB_SPLAT,
        payload: { seatIndex, id }
    };
}
function removeAllPlayerBombSplats(seatIndex) {
    return {
        type: REMOVE_ALL_PLAYER_BOMB_SPLATS,
        payload: seatIndex
    };
}
/**
 * Get a player's seat index from their player ID and poker state, and remove all bomb splats using that.
 */
export const thunkRemoveAllPlayerIdBombSplats = (playerId) => {
    return function (dispatch, getState) {
        const seatedPlayer = Object.values(getState().poker.seatedPlayers).find((currSeatedPlayer) => currSeatedPlayer?.id === playerId);
        if (seatedPlayer) {
            dispatch(removeAllPlayerBombSplats(seatedPlayer.seatIndex));
        }
    };
};
export function removeAllBombSplats() {
    return {
        type: REMOVE_ALL_BOMB_SPLATS
    };
}
function recalculateBombTrajectories(allSeatBounds, tableAreaDimensions) {
    return {
        type: RECALCULATE_BOMB_TRAJECTORIES,
        payload: { allSeatBounds, tableAreaDimensions }
    };
}
/**
 * If the stage dimensions change (on resize or if the active panel is toggled on and off),
 * recalculate all in-flight bomb trajectories so each will have a new path to the destination seat.
 */
export const thunkRecalculateBombTrajectories = () => {
    return function (dispatch, getState) {
        const state = getState();
        const allSeatBounds = state.gameDimensions.allSeatBounds;
        const tableAreaDimensions = state.gameDimensions.tableAreaDimensions;
        if (!allSeatBounds.length) {
            // not initialized, can't bomb
            console.warn('Seat bounds not initialized, could not recalculate bomb trajectories');
            return;
        }
        if (!tableAreaDimensions) {
            // not initialized, can't bomb
            console.warn('Table area dimensions not initialized, could not recalculate bomb trajectories');
            return;
        }
        dispatch(recalculateBombTrajectories(allSeatBounds, tableAreaDimensions));
    };
};
export function thunkBombSeat(seatIndex, bombType, numberOfBombs, socketSend, uiControlType) {
    return function (dispatch, getState) {
        const seatedPlayer = getState().poker.seatedPlayers[seatIndex];
        if (!seatedPlayer) {
            return;
        }
        throwBomb({ targetUsername: seatedPlayer.name, bombType, uiControlType, socketSend, numberOfBombs });
    };
}
export function setDraggingBombType(type) {
    return {
        type: SET_DRAGGING_BOMB_TYPE,
        payload: type
    };
}
export function setDraggingBombIsOverTarget(isOverTarget) {
    return {
        type: SET_DRAGGING_BOMB_IS_OVER_TARGET,
        payload: isOverTarget
    };
}
